import { getJson, postJson, requestJson, deleteJson } from './client';
import { loadAuthSession } from '../data/authSession';

// ── Enum 값 (백엔드 Region / Genre 코드와 1:1 대응) ─────────────────────────

export type Region =
  | 'SEOUL'
  | 'BUSAN'
  | 'DAEGU'
  | 'INCHEON'
  | 'GWANGJU'
  | 'DAEJEON'
  | 'ULSAN'
  | 'SEJONG'
  | 'GYEONGGI'
  | 'GANGWON'
  | 'CHUNGBUK'
  | 'CHUNGNAM'
  | 'JEONBUK'
  | 'JEONNAM'
  | 'GYEONGBUK'
  | 'GYEONGNAM'
  | 'JEJU';

export type Genre =
  | 'ROCK'
  | 'METAL'
  | 'PUNK'
  | 'BALLAD'
  | 'POP'
  | 'DANCE'
  | 'HIPHOP'
  | 'RNB'
  | 'JAZZ'
  | 'CLASSIC'
  | 'EDM'
  | 'INDIE'
  | 'ACOUSTIC'
  | 'FOLK';

export const REGION_LABELS: Record<Region, string> = {
  SEOUL: '서울특별시',
  BUSAN: '부산광역시',
  DAEGU: '대구광역시',
  INCHEON: '인천광역시',
  GWANGJU: '광주광역시',
  DAEJEON: '대전광역시',
  ULSAN: '울산광역시',
  SEJONG: '세종특별자치시',
  GYEONGGI: '경기도',
  GANGWON: '강원특별자치도',
  CHUNGBUK: '충청북도',
  CHUNGNAM: '충청남도',
  JEONBUK: '전북특별자치도',
  JEONNAM: '전라남도',
  GYEONGBUK: '경상북도',
  GYEONGNAM: '경상남도',
  JEJU: '제주특별자치도',
};

export const GENRE_LABELS: Record<Genre, string> = {
  ROCK: '록',
  METAL: '메탈',
  PUNK: '펑크',
  BALLAD: '발라드',
  POP: '팝',
  DANCE: '댄스',
  HIPHOP: '힙합',
  RNB: 'R&B',
  JAZZ: '재즈',
  CLASSIC: '클래식',
  EDM: 'EDM',
  INDIE: '인디',
  ACOUSTIC: '어쿠스틱',
  FOLK: '포크',
};

// ── DTO 타입 ─────────────────────────────────────────────────────────────────

/** BandResponse.java 필드와 1:1 대응 */
export type BandResponse = {
  bandId: number;
  name: string;
  introduction: string | null;
  genre: Genre | null;
  region: Region | null;
  ownerUserId: number;
  capacity: number;
  memberCount: number;
  createdAt: string;
  imageUrl: string | null;
};

/** POST /api/v1/bands 요청 바디 */
export type CreateBandInput = {
  name: string;
  introduction?: string;
  genre?: Genre;
  region?: Region;
  capacity: number;
  imageUrl?: string;
};

/** PUT /api/v1/bands/{id} 요청 바디 */
export type UpdateBandInput = {
  name?: string;
  introduction?: string;
  genre?: Genre;
  region?: Region;
  capacity?: number;
  imageUrl?: string;
};

/** MemberProfile.java 필드와 1:1 대응 */
export type MemberProfile = {
  userId: string;
  nickname: string;
  profileImageUrl: string | null;
};

/** BandMemberResponse.java 필드와 1:1 대응 */
export type BandMemberResponse = {
  userId: number;
  role: 'LEADER' | 'MEMBER';
  profile: MemberProfile;
};

/** 백엔드 CursorPageResponse<T> 계약 */
export type CursorPageResponse<T> = {
  items: T[];
  nextCursor: string | null;
  hasNext: boolean;
  size: number;
};

// ── API 함수 ─────────────────────────────────────────────────────────────────

export type SearchBandsParams = {
  region?: Region;
  genre?: Genre;
  name?: string;
  cursor?: string;
  size?: number;
};

/** GET /api/v1/bands — 커서 기반 밴드 검색 */
export function searchBands(params: SearchBandsParams = {}) {
  const query = new URLSearchParams();
  if (params.region) query.set('region', params.region);
  if (params.genre) query.set('genre', params.genre);
  if (params.name) query.set('name', params.name);
  if (params.cursor) query.set('cursor', params.cursor);
  if (params.size !== undefined) query.set('size', String(params.size));
  const qs = query.toString();
  return getJson<CursorPageResponse<BandResponse>>(`/api/v1/bands${qs ? `?${qs}` : ''}`, {
    preserveAuthOnUnauthorized: true,
  });
}

/** GET /api/v1/bands/{bandId} — 밴드 상세 */
export function getBand(bandId: number | string) {
  return getJson<BandResponse>(`/api/v1/bands/${encodeURIComponent(bandId)}`, {
    preserveAuthOnUnauthorized: true,
  });
}

/** GET /api/v1/bands/{bandId}/members — 멤버 목록 */
export function getBandMembers(bandId: number | string) {
  return getJson<BandMemberResponse[]>(`/api/v1/bands/${encodeURIComponent(bandId)}/members`, {
    preserveAuthOnUnauthorized: true,
  });
}

/** POST /api/v1/bands — 밴드 생성 */
export function createBand(input: CreateBandInput) {
  return postJson<BandResponse>('/api/v1/bands', input);
}

/** PUT /api/v1/bands/{id} — 밴드 수정 */
export function updateBand(bandId: number | string, input: UpdateBandInput) {
  return requestJson<BandResponse>(`/api/v1/bands/${encodeURIComponent(bandId)}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/** DELETE /api/v1/bands/{id} — 밴드 해산 */
export function disbandBand(bandId: number | string) {
  return deleteJson<void>(`/api/v1/bands/${encodeURIComponent(bandId)}`);
}

/**
 * 밴드 대표 이미지 업로드: grant 발급 → S3 PUT → commit → publicUrl 반환.
 *
 * targetType=BAND_IMAGE(media-service 에 추가됨), ownerType=USER 로 업로드한다.
 * 밴드 이미지는 유저 소유로 두고(미디어 소유권은 업로더 본인), 밴드 리더 권한 검증은
 * BandWorld_BE 가 밴드 생성/수정 시점에 수행한다.
 */
export async function uploadBandImage(file: File): Promise<string> {
  const { prepareImageForUpload, putAndCommit } = await import('./media');
  const uploadFile = await prepareImageForUpload(file);
  const grant = await postJson<{
    mediaRef: string;
    uploadUrl: string;
    publicUrl: string;
    uploadHeaders?: Record<string, string>;
    ownershipTicket?: string;
  }>('/api/v1/media/uploads', {
    contentLength: uploadFile.size,
    contentType: uploadFile.type,
    fileName: uploadFile.name,
    ownerKey: loadAuthSession()?.userId ?? null,
    ownerType: 'USER',
    targetType: 'BAND_IMAGE',
  });
  await putAndCommit({
    mediaId: grant.mediaRef,
    uploadUrl: grant.uploadUrl,
    uploadHeaders: grant.uploadHeaders,
    ownershipTicket: grant.ownershipTicket,
    file: uploadFile,
  });
  return grant.publicUrl ?? '';
}
